import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export interface GameProject {
  id: string;
  name: string;
  description: string;
  status: 'not_started' | 'questionnaire' | 'spec_generation' | 'design_generation' | 'tasks_generation' | 'ready' | 'building' | 'complete';
  createdAt: Date;
  updatedAt: Date;
  
  // Project metadata
  genre: string;
  platform: string[];
  templates: string[];
  
  // Generated content
  spec?: string;
  design?: string;
  tasks?: string;
  
  // Build information
  builds: Array<{
    id: string;
    platform: 'webgl' | 'android' | 'ios' | 'windows' | 'mac' | 'linux';
    status: 'pending' | 'building' | 'success' | 'failed';
    url?: string;
    createdAt: Date;
    logs?: string;
  }>;
  
  // Project files
  files: Array<{
    path: string;
    content: string;
    type: 'script' | 'scene' | 'prefab' | 'material' | 'texture' | 'audio';
    lastModified: Date;
  }>;
  
  // Unity project structure
  engine: 'unity' | 'unreal' | 'godot';
  engineVersion: string;
  projectType: '2D' | '3D' | 'VR' | 'AR';
  
  // Collaboration
  collaborators: Array<{
    userId: string;
    email: string;
    role: 'owner' | 'editor' | 'viewer';
  }>;
}

export interface ProjectState {
  // Current project
  currentProject: GameProject | null;
  setCurrentProject: (project: GameProject | null) => void;
  
  // All projects
  projects: GameProject[];
  setProjects: (projects: GameProject[]) => void;
  addProject: (project: GameProject) => void;
  updateProject: (id: string, updates: Partial<GameProject>) => void;
  removeProject: (id: string) => void;
  
  // Project operations
  loadProject: (projectId: string, engine: 'unity' | 'unreal' | 'godot') => Promise<void>;
  createProject: (name: string, description: string) => Promise<GameProject>;
  saveProject: () => Promise<void>;
  
  // Build operations
  buildProject: (platform: string) => Promise<void>;
  getBuildStatus: (buildId: string) => Promise<string>;
  
  // File operations
  updateFile: (path: string, content: string) => void;
  addFile: (file: { path: string; content: string; type: string }) => void;
  removeFile: (path: string) => void;
  
  // Editor state
  currentFile: { path: string; content: string; type: string; lastModified: Date } | null;
  openFile: (path: string) => void;
  closeFile: () => void;
  
  // Loading states
  isLoading: boolean;
  isBuilding: boolean;
  isSaving: boolean;
  
  // Error handling
  error: string | null;
  setError: (error: string | null) => void;
}

export const useProjectStore = create<ProjectState>()(
  immer((set, get) => ({
    // Current project
    currentProject: null,
    setCurrentProject: (project) => set((state) => {
      state.currentProject = project;
    }),
    
    // All projects
    projects: [],
    setProjects: (projects) => set((state) => {
      state.projects = projects;
    }),
    addProject: (project) => set((state) => {
      state.projects.push(project);
    }),
    updateProject: (id, updates) => set((state) => {
      const index = state.projects.findIndex(p => p.id === id);
      if (index >= 0) {
        Object.assign(state.projects[index], updates);
      }
      
      // Update current project if it's the same
      if (state.currentProject?.id === id) {
        Object.assign(state.currentProject, updates);
      }
    }),
    removeProject: (id) => set((state) => {
      state.projects = state.projects.filter(p => p.id !== id);
      if (state.currentProject?.id === id) {
        state.currentProject = null;
      }
    }),
    
    // Project operations
    loadProject: async (projectId: string, engine: 'unity' | 'unreal' | 'godot') => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });
      
      try {
        // Get token from auth store
        let token = localStorage.getItem('stan_token');
        if (!token) {
          throw new Error('No authentication token found in storage');
        }
        
        token = token.trim(); // CRITICAL: Remove trailing whitespace/newlines
        
        console.log(`[IDE] Initializing project load for ${projectId} (${engine})`);
        console.log(`[IDE] Token Length: ${token.length}, Start: ${token.substring(0, 10)}...`);
        
        const headers = {
          'Authorization': `Bearer ${token}`,
        };

        // 1. Verify token and get user context first
        console.log('[IDE] Verifying user context...');
        const contextRes = await fetch('https://stan-backend.stanleyisaac134.workers.dev/api/user/context', { headers });
        
        if (!contextRes.ok) {
          const errBody = await contextRes.text();
          console.error(`[IDE] Context verification failed: ${contextRes.status} - ${errBody}`);
          if (contextRes.status === 401) {
            throw new Error('Unauthorized: Your session may have expired. Please try opening from the dashboard again.');
          }
          // Continue anyway if it's not 401, maybe just a partial failure
        }

        // 2. Load the actual project
        console.log(`[IDE] Loading project data from backend...`);
        const response = await fetch(`https://stan-backend.stanleyisaac134.workers.dev/api/ide/project/${projectId}`, { headers });
        
        console.log(`[IDE] Project Response: ${response.status}`);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[IDE] Project load failed:', errorText);
          throw new Error(`Failed to load project (${response.status}): ${response.statusText}`);
        }
        
        const projectData = await response.json();
        console.log('[IDE] Project data received successfully');
        
        // Transform API response to our project format
        const project: GameProject = {
          id: projectData.id,
          name: projectData.name || projectData.title || 'Untitled Project',
          description: projectData.description || projectData.initial_prompt || '',
          status: projectData.status,
          createdAt: new Date(projectData.created_at),
          updatedAt: new Date(projectData.updated_at || projectData.created_at),
          genre: projectData.genre || 'Unknown',
          platform: projectData.platform || ['WebGL'],
          templates: projectData.templates || [],
          spec: projectData.spec,
          design: projectData.design,
          tasks: projectData.tasks,
          builds: projectData.builds || [],
          files: projectData.files || [],
          engine: engine,
          engineVersion: projectData.engine_version || '2022.3.0f1',
          projectType: projectData.project_type || '2D',
          collaborators: projectData.collaborators || [],
        };
        
        set((state) => {
          state.currentProject = project;
          state.isLoading = false;
          
          // Add to projects list if not already there
          const existingIndex = state.projects.findIndex(p => p.id === project.id);
          if (existingIndex >= 0) {
            state.projects[existingIndex] = project;
          } else {
            state.projects.push(project);
          }
        });
        
      } catch (error) {
        console.error('Failed to load project:', error);
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to load project';
          state.isLoading = false;
        });
      }
    },
    
    createProject: async (name: string, description: string): Promise<GameProject> => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });
      
      try {
        const token = localStorage.getItem('stan_token');
        if (!token) {
          throw new Error('No authentication token');
        }
        
        const response = await fetch('https://stan-backend.stanleyisaac134.workers.dev/api/projects/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            initial_prompt: description,
            title: name,
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to create project: ${response.statusText}`);
        }
        
        const projectData = await response.json();
        
        const project: GameProject = {
          id: projectData.projectId,
          name: name,
          description: description,
          status: 'not_started',
          createdAt: new Date(),
          updatedAt: new Date(),
          genre: 'Unknown',
          platform: ['WebGL'],
          templates: [],
          builds: [],
          files: [],
          unityVersion: '2022.3.0f1',
          projectType: '2D',
          collaborators: [],
        };
        
        set((state) => {
          state.currentProject = project;
          state.projects.push(project);
          state.isLoading = false;
        });
        
        return project;
        
      } catch (error) {
        console.error('Failed to create project:', error);
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to create project';
          state.isLoading = false;
        });
        throw error;
      }
    },
    
    saveProject: async () => {
      const { currentProject } = get();
      if (!currentProject) return;
      
      set((state) => {
        state.isSaving = true;
        state.error = null;
      });
      
      try {
        const token = localStorage.getItem('stan_token');
        if (!token) {
          throw new Error('No authentication token');
        }
        
        // Save project files and metadata
        const response = await fetch(`https://stan-backend.stanleyisaac134.workers.dev/api/projects/${currentProject.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: currentProject.name,
            description: currentProject.description,
            files: currentProject.files,
            status: currentProject.status,
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to save project: ${response.statusText}`);
        }
        
        set((state) => {
          state.isSaving = false;
          if (state.currentProject) {
            state.currentProject.updatedAt = new Date();
          }
        });
        
      } catch (error) {
        console.error('Failed to save project:', error);
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to save project';
          state.isSaving = false;
        });
      }
    },
    
    // Build operations
    buildProject: async (platform: string) => {
      const { currentProject } = get();
      if (!currentProject) return;
      
      set((state) => {
        state.isBuilding = true;
        state.error = null;
      });
      
      try {
        const token = localStorage.getItem('stan_token');
        if (!token) {
          throw new Error('No authentication token');
        }
        
        const response = await fetch(`https://stan-backend.stanleyisaac134.workers.dev/api/projects/${currentProject.id}/build`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            platform: platform,
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to start build: ${response.statusText}`);
        }
        
        const buildData = await response.json();
        
        set((state) => {
          if (state.currentProject) {
            state.currentProject.builds.push({
              id: buildData.buildId,
              platform: platform as any,
              status: 'building',
              createdAt: new Date(),
            });
          }
          state.isBuilding = false;
        });
        
      } catch (error) {
        console.error('Failed to build project:', error);
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to build project';
          state.isBuilding = false;
        });
      }
    },
    
    getBuildStatus: async (buildId: string): Promise<string> => {
      try {
        const token = localStorage.getItem('stan_token');
        if (!token) {
          throw new Error('No authentication token');
        }
        
        const response = await fetch(`https://stan-backend.stanleyisaac134.workers.dev/api/build/${buildId}/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to get build status: ${response.statusText}`);
        }
        
        const data = await response.json();
        return data.status;
        
      } catch (error) {
        console.error('Failed to get build status:', error);
        return 'failed';
      }
    },
    
    // File operations
    updateFile: (path: string, content: string) => set((state) => {
      if (!state.currentProject) return;
      
      const fileIndex = state.currentProject.files.findIndex(f => f.path === path);
      if (fileIndex >= 0) {
        state.currentProject.files[fileIndex].content = content;
        state.currentProject.files[fileIndex].lastModified = new Date();
        
        // Update current file if it's the one being modified
        if (state.currentFile?.path === path) {
          state.currentFile.content = content;
          state.currentFile.lastModified = new Date();
        }
      }
    }),
    
    addFile: (file) => set((state) => {
      if (!state.currentProject) return;
      
      const newFile = {
        path: file.path,
        content: file.content,
        type: file.type as any,
        lastModified: new Date(),
      };
      state.currentProject.files.push(newFile);
    }),
    
    removeFile: (path: string) => set((state) => {
      if (!state.currentProject) return;
      
      state.currentProject.files = state.currentProject.files.filter(f => f.path !== path);
      
      // Close file if it's open
      if (state.currentFile?.path === path) {
        state.currentFile = null;
      }
    }),
    
    // Editor state
    currentFile: null,
    openFile: (path: string) => set((state) => {
      if (!state.currentProject) return;
      const file = state.currentProject.files.find(f => f.path === path);
      if (file) {
        state.currentFile = file;
      }
    }),
    closeFile: () => set((state) => {
      state.currentFile = null;
    }),
    
    // Loading states
    isLoading: false,
    isBuilding: false,
    isSaving: false,
    
    // Error handling
    error: null,
    setError: (error) => set((state) => {
      state.error = error;
    }),
  }))
);